import { useCallback, useContext, useRef, useState } from "react";
import { toast } from "react-toastify";
import router from "next/router";
import { Button } from "components/atoms/Button";
import { ButtonType } from "components/atoms/Button/types";
import { Modal } from "components/atoms/Modal/index";
import Spinner from "components/atoms/Spinner";
import { editImage, poll, pollEditImage } from "lib/actions";
import { AppContext } from "lib/contexts";
import { AppReducerActionType, EditType, UserPremiumType } from "types";
import { Label, Span, Title } from "utils/styling/typography.styled";
import * as Styled from "./FaceSwapModal.styled";
interface FaceSwapModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (editRequest: string, isPremium: boolean, editType: string, goldCost: number, negativePrompt?: string, cfgScale?: number, numDetections?: number, urlOrFile?: string | File) => void;
  imageId: string;
}
export const FaceSwapModal: React.FC<FaceSwapModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  imageId
}) => {
  const {
    state,
    dispatch
  } = useContext(AppContext);
  const [url, setUrl] = useState<string>("");
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const sheetUrlRef = useRef<HTMLInputElement>(null);
  const resetFileInput = useCallback(() => {
    if (fileRef.current) {
      fileRef.current.value = "";
      setFile(null);
    }
  }, []);
  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUrl(e.target.value);
    setFile(null); // Clear file if URL is entered
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      setFile(e.target.files[0]);
      setUrl(""); // Clear URL if a file is selected
    }
  };
  const handleFaceSwapSubmit = useCallback(async (urlOrFile: string | File, imageId: string) => {
    if (!state.isLoggedIn) {
      dispatch({
        type: AppReducerActionType.SHOW_AUTH_MODAL,
        payload: {
          ...state,
          showAuthModal: true
        }
      });
      return;
    }
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    } else if (state.gold < 4) {
      dispatch({
        type: AppReducerActionType.SHOW_PRICING_TABLE,
        payload: {
          ...state,
          showPricingTable: true
        }
      });
      return;
    }
    const formData = new FormData();
    formData.append("imageId", imageId);
    formData.append("editType", EditType.FACESWAP);
    if (typeof urlOrFile === "string") {
      formData.append("faceImageUrl", urlOrFile);
    } else {
      formData.append("faceImageFile", urlOrFile);
    }
    setLoading(true);
    const {
      requestId,
      error
    } = await editImage(formData);
    if (error) {
      toast.error("An error occurred while submitting the face swap request.");
      setLoading(false);
      return;
    }
    if (requestId) {
      const {
        image,
        error
      } = await poll(async () => {
        const result = await pollEditImage(requestId);
        if (result.error && typeof result.error === "object") {
          console.log("Error details:", result.error);
        }
        return result;
      }, ({
        image,
        error
      }) => {
        // Continue polling only if there is no image and no critical error
        if (!!image) return false; // Stop polling when an image is retrieved
        if (typeof error === "string") return false;
        if (typeof error === "object") return false;
        return true; // Continue polling otherwise
      }, 5000);
      if (error) {
        if (typeof error === "object") {
          toast.error(error.message || "An unknown error occurred while generating the image and your gold has been refunded. Please try again.");
          dispatch({
            type: AppReducerActionType.SET_GOLD,
            payload: {
              ...state,
              gold: state.gold + 4
            }
          });
        } else if (typeof error === "string") {
          toast.error("The request timed out. Please try again.");
          dispatch({
            type: AppReducerActionType.SET_GOLD,
            payload: {
              ...state,
              gold: state.gold + 4
            }
          });
        }
      }
      if (image && image.length > 0) {
        const newImage = Array.isArray(image) ? image[0] : image;

        // Redirect to the new image's page
        router.push(`/images/${newImage.id}`);
        dispatch({
          type: AppReducerActionType.SET_GOLD,
          payload: {
            ...state,
            gold: state.gold - 4
          }
        });
      }
    }
    setLoading(false);
  }, [dispatch, state]);
  const handleSubmit = async () => {
    if (onSubmit) {
      const urlOrFile = file ? file : url;
      await onSubmit("Face Swap Request", false, EditType.FACESWAP, 4, undefined, undefined, undefined, urlOrFile);
      handleClose();
    } else {
      await handleFaceSwapSubmit(file || url, imageId);
      onClose();
    }
  };
  const handleClose = () => {
    setUrl("");
    resetFileInput();
    setLoading(false);
    onClose();
  };
  return <Modal open={isOpen} handleBackdropClick={onClose} disableBackdropClick data-sentry-element="Modal" data-sentry-component="FaceSwapModal" data-sentry-source-file="index.tsx">
            <Styled.FaceSwapModalContainer data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                <Title data-sentry-element="Title" data-sentry-source-file="index.tsx">Face Swap</Title>
                <hr />
                <Styled.FormGroup data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <Label htmlFor="url" data-sentry-element="Label" data-sentry-source-file="index.tsx">Face Image URL:</Label>
                    <Styled.InputWrapper data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                        <Styled.Input type="text" id="url" value={url} onChange={handleUrlChange} placeholder="Enter a URL for the face to swap" ref={sheetUrlRef} data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                    </Styled.InputWrapper>
                </Styled.FormGroup>
                <Styled.FormGroup data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <Styled.Label htmlFor="file" data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                        Or Upload a Face Image:
                    </Styled.Label>
                    <Styled.InputWrapper data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                        <Styled.Input type="file" ref={fileRef} id="file" accept="image/*" onChange={handleFileChange} data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                    </Styled.InputWrapper>
                </Styled.FormGroup>
                <Styled.GoldCost data-sentry-element="unknown" data-sentry-source-file="index.tsx">Gold Cost: 4 Gold</Styled.GoldCost>
                <Styled.ButtonContainer data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <Button type="button" variant={ButtonType.SUCCESS} onClick={handleSubmit} disabled={loading} // Disable the button while loading
        data-sentry-element="Button" data-sentry-source-file="index.tsx">
                        {loading ? <Spinner size={24} color="inherit" /> // Show spinner when loading
          : <Span>Submit</Span>}
                    </Button>
                    <Button type="button" variant={ButtonType.PRIMARY} onClick={handleClose} data-sentry-element="Button" data-sentry-source-file="index.tsx">
                        <Span data-sentry-element="Span" data-sentry-source-file="index.tsx">Cancel</Span>
                    </Button>
                </Styled.ButtonContainer>
            </Styled.FaceSwapModalContainer>
        </Modal>;
};